/**
 * Invoice V2 Models
 * TypeScript interfaces matching backend DTOs
 */

import { ForeignCurrencyPayload } from './currency.model';

// Enums
export enum InvoiceType {
  OFFER_PRICE = 0,
  ORDER = 100,
  MISSION = 200,
  TAX_INVOICE = 305,
  INVOICE_WITH_RECEIPT = 320,
  REVERSE_INVOICE = 330,
  FINANCIAL_REQUEST = 332,
  RECEIPT = 400,
  CANCELLED_RECEIPT = 430,
}

export enum PaymentType {
  CASH = 1,
  CHECK = 2,
  CREDIT_CARD = 3,
  BANK_TRANSFER = 4,
  DEDUCTION_FROM_SOURCE = 5,
}

/**
 * Payment method code enum (copied from legacy DOCUMENT module PAYMENTMETHODS)
 * Keeps same string codes used there: CASH, CHK, BT, CC
 */
export enum PaymentMethodCode {
  CASH = 'CASH',
  CHECK = 'CHK',
  BANK_TRANSFER = 'BT',
  CREDIT_CARD = 'CC',
}

/**
 * Credit card payment type enum (copied from legacy CreditCardPaymentFormComponent.paymentTypeOptions)
 * Represents how the credit transaction is processed.
 */
export enum CreditCardPaymentType {
  NOT_CREDIT_CARD = 0,
  NORMAL = 1,
  PAYMENTS = 2,
  CREDIT = 3,
  DEFERRED_PAYMENT = 4,
  OTHERS = 5,
}

/**
 * Credit card company/type enum (copied from legacy CreditCardPaymentFormComponent.creditTypeOptions)
 * These values represent the credit card company/type.
 */
export enum CreditCardType {
  NOT_CREDIT_CARD = 0,
  ISRACARD = 1,
  CAL = 2,
  DINERS = 3,
  AMERICAN_EXPRESS = 4,
  LEUMI_CARD = 6,
}

// Invoice status filter values (numeric) for invoices-v2
// 0 = saved, 1 = confirmed, 2 = deleted
export enum InvoiceStatus {
  SAVED = 0,
  CONFIRMED = 1,
  DELETED = 2,
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** Stored in DB — user-editable only (`paid` / `unpaid`). */
export type StoredPaymentStatus = 'paid' | 'unpaid';

/** Computed by backend — read-only in responses and filter queries. */
export type EffectivePaymentStatus =
  | StoredPaymentStatus
  | 'due_soon'
  | 'overdue'
  | null;

export interface InvoicePaymentFields {
  due_date: string | null;
  payment_status: StoredPaymentStatus;
  effective_payment_status: EffectivePaymentStatus;
  payment_status_label_ar: string | null;
  payment_status_label_en: string | null;
  payment_status_label_he: string | null;
}

export interface UpdateInvoicePaymentBody {
  payment_status?: StoredPaymentStatus;
  due_date?: string | null;
}

export interface UpdateInvoicePaymentResponse {
  success: boolean;
  message?: string;
  messageAr?: string;
  messageEn?: string;
  messageHe?: string;
  invoice_id?: number;
  data?: InvoicePaymentFields & {
    invoice_id?: number;
    confirm?: number;
    updatedDate?: string | Date;
    updated_by?: number;
  };
}

// Invoice V2 status codes (for success/error handling on frontend)
export enum InvoiceStatusCode {
  // Success (1000–1999)
  INVOICE_CREATED = 1000,
  INVOICE_CREATED_AND_CONFIRMED = 1001,
  RECEIPT_CREATED = 1002,
  INVOICE_UPDATED = 1003,
  INVOICE_CONVERTED = 1004,
  REVERSE_INVOICE_CREATED = 1005,

  // Validation errors (2000–2999)
  VALIDATION_ERROR = 2000,
  ITEMS_VALIDATION_ERROR = 2001,
  PAYMENTS_VALIDATION_ERROR = 2002,
  COMPANY_OWNERSHIP_ERROR = 2003,

  // Logical/state errors (3000–3999)
  INVOICE_NOT_FOUND = 3000,
  INVOICE_ALREADY_CONFIRMED = 3001,
  INVOICE_CANNOT_BE_CONVERTED = 3002,
  INVOICE_CANNOT_BE_REVERSED = 3003,

  // Tax authority / document number (4000–4999)
  DOCUMENT_NUMBER_GENERATION_FAILED = 4000,
  TAX_AUTHORITY_ERROR = 4001,
  CUSTOMER_VAT_REQUIRED = 4002,
  CUSTOMER_VAT_INVALID = 4003,
}

/** Structured frontend validation error for invoice v2 form */
export interface InvoiceFieldError {
  errorCode: string;
  source: 'frontend' | 'backend';
  section: 'header' | 'item' | 'payment' | 'customer' | 'tax' | 'business';
  fieldPath: string;
  row?: number;
  paymentIndex?: number;
  messageAr: string;
  messageEn: string;
  messageHe: string;
  correlationId: string;
}

export interface InvoiceValidationContext {
  formValue: Record<string, any>;
  hasItems: boolean;
  hasPayments: boolean;
  confirm: boolean;
  allocationNumber: number;
  paymentMethodsList: Array<{
    payment_method_code: number | string;
    status?: number;
    required_fields?: string[];
  }>;
  selectedCustomer: Record<string, any> | null;
  selectedCompany: Record<string, any> | null;
  allowedInvoiceTypes: number[] | null;
  correlationId: string;
}

// Generic multi-language payload shape for invoice V2 responses
export interface MultiLangMessagePayload {
  success: boolean;
  statusCode: InvoiceStatusCode;
  messageAr: string;
  messageEn: string;
  messageHe: string;
  message?: string;
  errors?: string[];
  [key: string]: any;
}

// Payment Method Details
export interface PaymentMethodDetailsDto {
  date?: Date | string;
  reference?: string;
  bank?: string;
  branch?: string;
  account?: string;
  details?: string;
  credit_card_code?: string;
  credit_card_name?: string;
  credit_type?: string;
  account_number?: string;
  line_id?: number | string;
}

// Payment Method
export interface PaymentMethodDto {
  type: number; // 1=Cash, 2=Check, 3=Credit Card, 4=Bank Transfer
  amount: number;
  details?: PaymentMethodDetailsDto;
}

// Sold Item
export interface CreateSoldItemV2Dto {
  index?: number;
  catalog_id?: string; // Max 13 chars
  category?: string;
  measurement_unit_id?: number;
  description?: string; // Max 300 chars
  item_name: string; // Required, 1-300 chars
  measure_unit_description?: string; // Max 20 chars
  quantity: number; // Required, > 0
  price_per_unit: number; // Required, > 0
  sub_total: number; // Required
  discount: number; // Required, >= 0
  total_amount: number; // Required
  vat_rate: number; // Required
  vat_amount: number; // Required
  product_id: number; // Required
  isPresenting?: boolean;
  isVatIncluded?: boolean;
  /** Foreign currency line amounts — required when invoice currency !== ILS */
  price_per_unit_foreign?: number;
  discount_foreign?: number;
  sub_total_foreign?: number;
  vat_amount_foreign?: number;
  total_amount_foreign?: number;
}

// Create Invoice DTO
export interface CreateInvoiceV2Dto {
  company_id: number; // Required
  customer_id: number; // Required
  invoice_type: number; // Required
  invoice_date: Date | string; // Required (ISO date string)
  document_language: string; // Required ('ar', 'en', 'he')
  /** Selected document currency (USD/EUR/GBP/ILS). Backend stores accounting currency as ILS on `invoices`. */
  currency?: string;
  foreign_currency?: ForeignCurrencyPayload; // Required when selected currency !== ILS
  allocation_number?: number; // Optional (0=no tax, 1=with tax)
  due_date?: string | null; // Optional YYYY-MM-DD
  payment_status?: StoredPaymentStatus; // Optional, default unpaid
  confirm?: boolean; // Optional (auto-confirm on creation)
  items: CreateSoldItemV2Dto[]; // Required (at least 1 item)
  payment_methods?: PaymentMethodDto[]; // Required if invoice_type = 320 or 400
  invoice_note?: string; // Optional
  // Optional amounts (auto-calculated if not provided):
  amount_before_discount?: number;
  all_discount?: number;
  payment_amount?: number;
  all_vat_amount?: number;
  payment_amount_including_vat?: number;
  total_amount?: number;
  deduction_from_source?: number;
}

// Update Invoice DTO (all fields optional for partial update)
export interface UpdateInvoiceV2Dto {
  company_id?: number;
  customer_id?: number;
  invoice_type?: number;
  invoice_date?: Date | string;
  document_language?: string;
  currency?: string;
  foreign_currency?: ForeignCurrencyPayload;
  allocation_number?: number;
  due_date?: string | null;
  payment_status?: StoredPaymentStatus;
  confirm?: boolean; // If true, will confirm after update
  items?: CreateSoldItemV2Dto[]; // Replaces all items if provided
  payment_methods?: PaymentMethodDto[]; // Replaces all payment methods if provided
  invoice_note?: string;
  amount_before_discount?: number;
  all_discount?: number;
  payment_amount?: number;
  all_vat_amount?: number;
  payment_amount_including_vat?: number;
  total_amount?: number;
  deduction_from_source?: number;
}

/**
 * Query params for GET /invoices-v2/filter (all optional).
 * sortIndex: 0=creation date, 1=invoice type, 2=deleted flag, 3=document #, 4=confirmation #,
 * 5=invoice date, 6=company name, 7=customer name, 8=amount, 9=due_date, 10=payment_status,
 * 11=last modified (updatedDate)
 */
export interface FilterInvoicesV2Dto {
  fromDate?: string; // YYYY-MM-DD
  toDate?: string; // YYYY-MM-DD
  dueDateFrom?: string; // YYYY-MM-DD
  dueDateTo?: string; // YYYY-MM-DD
  has_due_date?: boolean;
  payment_status?: EffectivePaymentStatus;
  payment_statuses?: string; // e.g. "overdue,due_soon"
  companyId?: number;
  customer_id?: number;
  invoice_type?: number;
  /** GET filter: multiple types, e.g. [200, 332] → query invoice_types=200,332 */
  invoice_types?: number[];
  status?: InvoiceStatus; // 0 saved, 1 confirmed, 2 deleted
  confirm?: boolean;
  is_deleted?: boolean;
  searchKeyword?: string;
  sort?: SortOrder; // ASC | DESC
  sortIndex?: number; // 0–11
  limit?: number; // 1–100, default 10
  offset?: number; // ≥0, default 0
  minAmount?: number;
  maxAmount?: number;
  document_number?: string;
  confirmation_number?: string;
  is_in_procedure_cycle?: boolean;
  /** Filter by selected document currency (from invoice_foreign_currency). `ILS` = no FX row. */
  currency?: string;
  /** Comma-separated document currencies, e.g. USD,EUR,ILS */
  currencies?: string;
  /** Optional subset of invoice IDs (e.g. Excel export of selected rows). */
  invoice_ids?: number[];
}

// Invoice Response DTO
export interface InvoiceResponseDto {
  invoice_id: number;
  document_number: string;
  confirmation_number?: string; // Present if confirmed and sent to tax authority
  confirm: number; // 0 = saved, 1 = confirmed
  pdf_link?: string; // Present if PDF generated
  message: string; // Success message
  data?: any; // Full invoice object with relations
  warnings?: string[]; // Amount validation warnings if amounts differ
  timing?: {
    creation_time_ms?: number;
    confirmation_time_ms?: number;
    government_send_time_ms?: number;
    pdf_generation_time_ms?: number;
    total_time_ms?: number;
  };
}

// Invoice List Response DTO (GET /invoices-v2/filter)
export interface InvoiceListResponseDto {
  status: number;
  messageAr: string;
  messageEn: string;
  messageHe: string;
  data: any[];
  total: number;
  limit?: number;
  offset?: number;
  /** Present on some backends; otherwise derive page from offset/limit */
  page?: number;
}

// Invoice Statistics Response
export interface InvoiceStatsResponse {
  status: number;
  messageAr: string;
  messageEn: string;
  messageHe: string;
  data: {
    total: number; // Total invoices
    confirmed: number; // Confirmed invoices
    saved: number; // Unconfirmed invoices
    deleted: number; // Deleted invoices
    totalAmount: number; // Sum of confirmed invoice amounts
    averageAmount: number; // Average of confirmed invoice amounts
  };
}

/** PUT /invoices-v2/:invoiceId/allocation-number (unconfirmed invoices only) */
export interface UpdateAllocationNumberResponse {
  success: boolean;
  messageAr?: string;
  messageEn?: string;
  messageHe?: string;
  invoice_id?: number;
  allocation_number?: number;
}

/** POST /invoices-v2/bulk-convert */
export interface BulkConvertInvoicesResponse {
  success: boolean;
  statusCode?: number;
  message?: string;
  messageAr?: string;
  messageEn?: string;
  messageHe?: string;
  invoice_id?: number;
  document_number?: string;
  confirm?: number;
  data?: unknown;
}

/** PATCH /invoices-v2/bulk/mark-merged */
export interface MarkInvoicesMergedResponse {
  success: boolean;
  updated: number;
  not_found: number[];
}

// Document Number Predict Response
export interface DocumentNumberPredictResponse {
  company_id: number;
  invoice_type: number;
  predicted_document_number: string;
  message: string;
}

// Document Number Generate Response
export interface DocumentNumberGenerateResponse {
  company_id: number;
  invoice_type: number;
  document_number: string;
}

// Document Number Stats Response
export interface DocumentNumberStatsResponse {
  lastDocumentNumber: string | null; // Last used document number
  totalConfirmed: number; // Total confirmed invoices with document numbers
  hasSequence: boolean; // Whether sequence exists
  sequenceStart: string; // Starting number from company settings
}

// Check Sequence Response
export interface CheckSequenceResponse {
  company_id: number;
  invoice_type: number;
  has_sequence: boolean;
}

/** Tax authority procedure decisions (lookup.enum.ts proceduresInvoice) */
export enum ProceduresInvoice {
  DecisionContinue = 1,
  DecisionFurtherObjection = 2,
  DecisionCancel = 3,
  DecisionReversingTheCharge = 4,
}

// Tax Authority Action Response
export interface TaxAuthorityActionResponse {
  status: number; // 200 = success, 400 = error, 500 = server error
  message?: string;
  messageEn?: string;
  messageAr?: string;
  messageHe?: string;
  link?: string; // For further objection (tax authority portal link)
  confirmation_number?: string; // For after hearing approval
  Confirmation_Number?: string; // For send second
  data?: {
    message?: string;
    zeroTaxInvoice?: { invoice_id?: number; confirmation_number?: string; status?: string };
    originalInvoice?: { invoice_id?: number; status?: string };
    reverseInvoice?: Record<string, unknown>;
    [key: string]: unknown;
  };
  details?: any; // Error details if failed
}

// Reverse Invoice Response
export interface ReverseInvoiceResponse {
  status: number; // 200 = success, 405 = in procedure cycle
  message: string;
  messageAr?: string;
  messageEn?: string;
  messageHe?: string;
  data?: any; // Reverse invoice data
  reverse_invoice_id?: number;
}

// Full Invoice Entity (from backend)
export interface Invoice {
  invoice_id: number;
  invoice_uuid: string;
  document_number: string;
  confirmation_number?: string;
  confirm: number;
  invoice_type: number;
  invoice_date: Date | string;
  document_language: string;
  company_id: number;
  customer_id: number;
  merchant_id: number;
  allocation_number: number;
  invoice_note?: string;
  amount_before_discount: number;
  all_discount: number;
  payment_amount: number;
  all_vat_amount: number;
  payment_amount_including_vat: number;
  total_amount: number;
  deduction_from_source: number;
  cash_amount: number;
  checks_amount: number;
  bank_payment_amount: number;
  credit_amount: number;
  pdf_link?: string;
  is_deleted: number;
  is_in_procedure_cycle: boolean;
  due_date?: string | null;
  payment_status?: StoredPaymentStatus;
  effective_payment_status?: EffectivePaymentStatus;
  payment_status_label_ar?: string | null;
  payment_status_label_en?: string | null;
  payment_status_label_he?: string | null;
  procedure?: number;
  invoice_category?: string;
  invoice_reference_number?: string;
  original_invoice_id?: number;
  created_by?: number;
  updated_by?: number;
  createdDate?: Date;
  updatedDate?: Date;
  soldItems?: any[];
  receiptsDetails?: any[];
  company?: any;
  customer?: any;
  merchant?: any;
}

